/**
 * Permission-based component visibility controls
 * 
 * These components provide declarative permission checking for UI elements,
 * allowing components to be conditionally rendered based on user permissions.
 */

import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/utils/permissions';

interface PermissionGuardProps {
  children: React.ReactNode;
  /**
   * Single permission required to show the component
   */
  permission?: Permission;
  /**
   * Multiple permissions - user must have ANY of these
   */
  anyOf?: Permission[];
  /**
   * Multiple permissions - user must have ALL of these
   */
  allOf?: Permission[];
  /**
   * Fallback component to render when permission is denied
   */
  fallback?: React.ReactNode;
  /**
   * Custom permission check function
   */
  check?: (permissions: { can: (p: Permission) => boolean; canAny: (p: Permission[]) => boolean; canAll: (p: Permission[]) => boolean }) => boolean;
}

/**
 * Component that conditionally renders children based on user permissions
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  anyOf,
  allOf,
  fallback = null,
  check,
}) => {
  const { can, canAny, canAll } = usePermissions();
  
  let hasAccess = false;
  
  if (check) {
    hasAccess = check({ can, canAny, canAll });
  } else if (permission) {
    hasAccess = can(permission);
  } else if (anyOf) {
    hasAccess = canAny(anyOf);
  } else if (allOf) {
    hasAccess = canAll(allOf);
  } else {
    // No permission specified, allow access
    hasAccess = true;
  }
  
  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Higher-order component for permission-based rendering
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: Permission | Permission[],
  fallback?: React.ReactNode
) {
  return function PermissionWrappedComponent(props: P) {
    const guardProps = Array.isArray(permission)
      ? { anyOf: permission }
      : { permission };
    
    return (
      <PermissionGuard {...guardProps} fallback={fallback}>
        <Component {...props} />
      </PermissionGuard>
    );
  };
}

/**
 * Component for showing content only to specific roles
 */
interface RoleGuardProps {
  children: React.ReactNode;
  roles: string[];
  fallback?: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  roles,
  fallback = null,
}) => {
  const { role } = usePermissions();
  
  const hasAccess = role && roles.includes(role);
  
  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component for authenticated users only
 */
interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallback = null,
}) => {
  const { isAuthenticated } = usePermissions();
  
  return isAuthenticated ? <>{children}</> : <>{fallback}</>;
};